import { Badges } from '@Components/Badges';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { LockPartnershipButton } from '@Components/LockPartnershipButton';
import { ImportExternalErpContactsFlow } from '@Components/Stammdaten';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { addressColumns, contactColumns, paymentTermsColumns } from '@Constants/TableColumns';
import { useHasPermission, usePortalEnabled } from '@Contexts/appContext';
import { useInviteToPartnership, useIsThisEntityNotInUse, usePartnershipInfo } from '@Hooks/Permissions';
import { useBadges } from '@Hooks/useBadges';
import type { ApiAddress, ApiContact, ApiPaymentInfo, ApiPaymentTerms, EnumPrintoutMergeStrategy } from '@Model';
import {
    AddressLinkLists,
    EnumAccountGroup,
    EnumAddressRole,
    EnumBusinessState,
    enumBusinessStateOptions,
    EnumFrontendLayoutSetting,
    EnumPartnershipType,
    mergeStrategyOptions,
    minimalAccountGroupOptions,
} from '@Model';
import { FrameEditContact, FrameEditPaymentInfo, FrameEditPaymentTerms } from '@Modules/Stammdaten';
import {
    Button,
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    FlexRow,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    Group,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    type IFrameHeaderProps,
    MultiPageView,
    MultiSelect,
    RailLayout,
    RailSection,
    Select,
    type SelectOption,
    SingleSelect,
    TextInput,
    type ToolbarOptions,
    UnlabeledField,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkedList,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { getContactColumnsWithPortalInvite } from '../../../columns/contactColumnsWithPortalInvite';
import { AddressLocalityFields } from './Address/AddressLocalityFields';
import {
    linksSummary,
    paymentSummary,
    registrationNumbersSummary,
    remarksSummary,
    roleLabels,
} from './Address/addressSummaries';

const roles: SelectOption<EnumAddressRole>[] = [
    { name: 'debtor', label: roleLabels[EnumAddressRole.Debtor], value: EnumAddressRole.Debtor },
    { name: 'creditor', label: roleLabels[EnumAddressRole.Creditor], value: EnumAddressRole.Creditor },
    {
        name: 'interested',
        label: roleLabels[EnumAddressRole.InterestedParty],
        value: EnumAddressRole.InterestedParty,
    },
    { name: 'origin', label: roleLabels[EnumAddressRole.Origin], value: EnumAddressRole.Origin },
    { name: 'disposal', label: roleLabels[EnumAddressRole.Disposal], value: EnumAddressRole.Disposal },
    { name: 'carrier', label: roleLabels[EnumAddressRole.Carrier], value: EnumAddressRole.Carrier },
    { name: 'private', label: roleLabels[EnumAddressRole.Private], value: EnumAddressRole.Private },
];

const preferPaymentOptions: SelectOption<boolean>[] = [
    { label: 'Standard', value: false, name: 'standard' },
    { label: 'Bar', value: true, name: 'cash' },
];

export function FrameEditAddress(props: IFrameEditShellProps<ApiAddress>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const frameOptions = props.frameOptions;

    // The same condition the frame body hands to the rail: an embedded frame renders none.
    const embedded = props.embedded === true || props.primaryKey !== undefined;

    const frameProps = useMemo<Partial<IFrameBodyProps<ApiAddress>>>(() => {
        return {
            frameOptions,
            events: {
                ...frameEditBaseNavigationEvents,
                ...props.events,
            },
        };
    }, [frameEditBaseNavigationEvents, props.events, frameOptions]);
    return (
        <FrameEditBase<ApiAddress>
            key={primaryKey}
            primaryKey={primaryKey}
            include={['payment_info']}
            embedded={embedded}
            apiResourcePath={`${STAMMDATEN_API}/addresses`}
            renderer={FrameEditAddressBody}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenAddressesUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenAddressesCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenAddressesDelete)}
            frameProps={frameProps}
        />
    );
}

function FrameEditAddressBody(props: IFrameBodyProps<ApiAddress>) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();
    const portalEnabled = usePortalEnabled();

    const apiAddress = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${apiAddress.id}`;
    const canReadPartnerships = hasPermission(Permission.PermissionsPartnershipsRead);
    const canInvitePortalUsers =
        portalEnabled && canReadPartnerships && hasPermission(Permission.PermissionsPartnershipsCreate);
    const canImportErpContacts =
        !!apiAddress.external_id && hasPermission(Permission.StammdatenExternalErpImportFromErp);

    const isViewMode = props.mode === EditFrameMode.View;
    const canEdit = !isViewMode;

    const isNewAddress = props.mode === EditFrameMode.Create;

    const binder = useGuiBinder<ApiAddress>({
        ...props,
        canEdit,
    });

    const canDelete = useIsThisEntityNotInUse(apiAddress, 'addresses');

    const {
        partnership: portalPartnership,
        existingUserEmails,
        pendingUserEmails,
        rejectedUserEmails,
        refetch: refetchPartnership,
    } = usePartnershipInfo(apiAddress.id, EnumPartnershipType.Portal, !canEdit && canReadPartnerships);
    const { mutateAsync: inviteToPartnership } = useInviteToPartnership(refetchPartnership);

    const [contactsCount, setContactsCount] = useState<number>(0);
    const [customersCount, setCustomersCount] = useState<number>(0);
    const [locationsCount, setLocationsCount] = useState<number>(0);
    const [isImportContactsOpen, setIsImportContactsOpen] = useState(false);

    const contactColumnsWithPortalInvite = useMemo(
        () =>
            getContactColumnsWithPortalInvite({
                isPortalEnabled: canInvitePortalUsers,
                customerAddress: apiAddress,
                isInviteLocked: !isViewMode,
                inviteToPartnership,
                existingPortalEmails: existingUserEmails,
                pendingPortalEmails: pendingUserEmails,
                rejectedPortalEmails: rejectedUserEmails,
            }),
        [
            canInvitePortalUsers,
            apiAddress,
            isViewMode,
            inviteToPartnership,
            existingUserEmails,
            pendingUserEmails,
            rejectedUserEmails,
        ],
    );

    const isPrivate = apiAddress.roles.includes(EnumAddressRole.Private);

    const isProducer = apiAddress.roles.includes(EnumAddressRole.Origin);
    const isCarrier = apiAddress.roles.includes(EnumAddressRole.Carrier);
    const isDisposer = apiAddress.roles.includes(EnumAddressRole.Disposal);

    const debtorInfo = apiAddress.payment_info.find((info) => info.role == EnumAddressRole.Debtor);
    const creditorInfo = apiAddress.payment_info.find((info) => info.role == EnumAddressRole.Creditor);
    const isCustomer = debtorInfo !== undefined || creditorInfo !== undefined;

    const linkPaymentTermsExpense = useLinkedFieldInFrame(props, 'payment_terms_expense', FrameEditPaymentTerms);
    const linkPaymentTermsRevenue = useLinkedFieldInFrame(props, 'payment_terms_revenue', FrameEditPaymentTerms);

    const {
        refetchRef: refetchContactsRef,
        linkListApi: linkContactsApi,
        linkedObjectOverlayEvents: contactsOverlayEvents,
    } = useLinkedList<ApiAddress, ApiContact>(
        baseApiObjectPath,
        AddressLinkLists.contacts,
        canEdit && hasPermission(Permission.StammdatenContactCreate),
        FrameEditContact,
    );
    const {
        refetchRef: refetchCustomersRef,
        linkListApi: linkCustomersApi,
        linkedObjectOverlayEvents: customersOverlayEvents,
    } = useLinkedList<ApiAddress, ApiAddress>(
        baseApiObjectPath,
        AddressLinkLists.customers,
        canEdit && hasPermission(Permission.StammdatenAddressesCreate),
        FrameEditAddress,
    );
    const {
        refetchRef: refetchLocationsRef,
        linkListApi: linkLocationsApi,
        linkedObjectOverlayEvents: locationsOverlayEvents,
    } = useLinkedList<ApiAddress, ApiAddress>(
        baseApiObjectPath,
        AddressLinkLists.locations,
        canEdit && hasPermission(Permission.StammdatenAddressesCreate),
        FrameEditAddress,
    );

    const { requestUpdateSubObject: requestUpdatePaymentInfo } = useEditSubObject<ApiPaymentInfo, ApiAddress>(
        baseApiObjectPath,
        'payment_info',
        props.requestUpdateState,
    );

    // Postal code to city automation: only the persistence hangs here, the logic lives in the
    // locality row. It writes through `saveData` like any committed field would: the automation
    // replaces an entry the user would otherwise have made, and no field commit follows it.
    const { requestUpdateState } = props;
    const applyCity = useCallback(
        (city: string) => {
            void requestUpdateState((latest, saveData) =>
                latest.city == city ? undefined : saveData({ ...latest, city }),
            );
        },
        [requestUpdateState],
    );

    const contactsTableProps = useMemo(
        () => ({
            onLoaderDone: ({ totalItems }: { totalItems: number }) => {
                setContactsCount(totalItems);
            },
            toolbarElements: canImportErpContacts
                ? (standard: ToolbarOptions[]) => [
                      ...standard,
                      <Button
                          key="import-erp-contacts"
                          onClick={() => setIsImportContactsOpen(true)}
                          variant="secondary"
                      >
                          Kontakte importieren
                      </Button>,
                  ]
                : undefined,
        }),
        [canImportErpContacts],
    );

    const linkLists = [
        <Group key="contacts" name="contacts" label={i18n('Ansprechpartner') + ` (${contactsCount})`}>
            <LinkedListWithLayoutSettings<ApiContact>
                layoutSettingsContext={EnumFrontendLayoutSetting.ContactsTableLayout}
                layoutSettingsModalContext={EnumFrontendLayoutSetting.ContactsTableLayout}
                {...binder.bindToLinkList(linkContactsApi)}
                {...contactsOverlayEvents}
                refetchRef={refetchContactsRef}
                columns={contactColumnsWithPortalInvite}
                modalColumns={contactColumns}
                tableProps={contactsTableProps}
            />
            {canImportErpContacts && apiAddress.external_id && (
                <ImportExternalErpContactsFlow
                    isOpen={isImportContactsOpen}
                    onClose={() => setIsImportContactsOpen(false)}
                    externalAddressId={apiAddress.external_id}
                    onImported={() => refetchContactsRef.current?.()}
                />
            )}
        </Group>,
        <Group key="customers" name="customers" label={i18n('Kunden') + ` (${customersCount})`}>
            <LinkedListWithLayoutSettings<ApiAddress>
                layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                layoutSettingsModalContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                {...binder.bindToLinkList(linkCustomersApi)}
                {...customersOverlayEvents}
                refetchRef={refetchCustomersRef}
                columns={addressColumns}
                tableProps={{
                    onLoaderDone: ({ totalItems }) => {
                        setCustomersCount(totalItems);
                    },
                }}
            />
        </Group>,
    ];

    if (isCustomer) {
        linkLists.push(
            <Group key="locations" name="locations" label={i18n('Leistungsorte') + ` (${locationsCount})`}>
                <LinkedListWithLayoutSettings<ApiAddress>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    {...binder.bindToLinkList(linkLocationsApi)}
                    {...locationsOverlayEvents}
                    refetchRef={refetchLocationsRef}
                    columns={addressColumns}
                    tableProps={{
                        onLoaderDone: ({ totalItems }) => {
                            setLocationsCount(totalItems);
                        },
                    }}
                />
            </Group>,
        );
    }

    const addressBadges = useBadges(apiAddress);
    const debitorBadges = useBadges(debtorInfo);
    const creditorBadges = useBadges(creditorInfo);
    const roleBadges = apiAddress.roles.map((role) => ({ children: roleLabels[role], variant: 'primary' as const }));

    const frameActionItems = props.frameOptions?.actionItems;

    const combinedActionItems = useMemo(() => {
        const items = frameActionItems ? [...frameActionItems] : [];
        items.push(
            <LockPartnershipButton
                key="lock_partnership"
                partnership={portalPartnership}
                refetchPartnership={refetchPartnership}
            />,
        );
        return items;
    }, [frameActionItems, portalPartnership, refetchPartnership]);

    const headerProps = useMemo<IFrameHeaderProps>(
        () => ({
            label: getFrameLabel('Adresse', props.mode),
            resourceLabel: 'Adresse',
            ...props.events,
            actionItems: combinedActionItems,
            canDelete,
        }),
        [props.events, props.mode, combinedActionItems, canDelete],
    );
    const bannerProps = useMemo(
        () => ({
            mode: props.mode,
            loading: props.stateQueueDepth > 0,
            ...props.events,
        }),
        [props.events, props.mode, props.stateQueueDepth],
    );

    return (
        <EditFrameShell headerProps={headerProps} bannerProps={bannerProps}>
            <RailLayout<ApiAddress>
                caption={i18n('Abschnitte')}
                showRail={!props.embedded}
                meta={props.meta}
                data={apiAddress}
                readOnly={props.mode === EditFrameMode.View}
            >
                <RailSection
                    id="base-data"
                    title="Basisdaten & Rollen"
                    additionalHeaderItems={<Badges badges={[...addressBadges, ...roleBadges]} />}
                >
                    <Grid columns={18}>
                        <GridColumn span={18}>
                            <MultiSelect<EnumAddressRole>
                                items={roles}
                                {...binder.bindToAutoConfirmed<EnumAddressRole[]>('roles')}
                                label={i18n('Rollen')}
                                values={apiAddress.roles}
                                variant="inline"
                            />
                        </GridColumn>
                        <GridColumns spans={[12, 6]}>
                            <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                            <Select
                                items={enumBusinessStateOptions}
                                {...binder.bindToAutoConfirmed<EnumBusinessState>('business_state')}
                                label={i18n('Status')}
                            />
                        </GridColumns>
                        <GridColumn span={18}>
                            <TextInput {...binder.bindToProp<string>('label')} label={i18n('Name')} inputSize={2} />
                        </GridColumn>
                        <GridColumns spans={[13, 5]}>
                            <TextInput {...binder.bindToProp<string>('street')} label={i18n('Straße')} />
                            <TextInput {...binder.bindToProp<string>('house_number')} label={i18n('Haus Nr.')} />
                        </GridColumns>
                        <AddressLocalityFields
                            binder={binder}
                            apiAddress={apiAddress}
                            applyCity={applyCity}
                            mode={props.mode}
                        />
                    </Grid>

                    <Card name="contact" label="Kontakt (optional)" border={'withoutBorder'} padding="none">
                        <Grid columns={18}>
                            <GridColumns spans={[6, 6, 6]}>
                                <TextInput
                                    type="tel"
                                    {...binder.bindToProp<string>('phone')}
                                    label={i18n('Telefon-Nr.')}
                                />
                                <TextInput
                                    type="email"
                                    {...binder.bindToProp<string>('email')}
                                    label={i18n('E-Mail')}
                                />
                                <TextInput
                                    type="url"
                                    {...binder.bindToProp<string>('website')}
                                    label={i18n('Website')}
                                />
                            </GridColumns>
                        </Grid>
                    </Card>
                </RailSection>

                {isCustomer && (
                    <RailSection id="payment" title="Zahlungsinformationen" subtitle={paymentSummary(apiAddress)}>
                        <Grid columns={12}>
                            <GridColumns spans={[4, 5, 3]}>
                                {!isPrivate && (
                                    <TextInput {...binder.bindToProp('tax_number')} label="Umsatzsteuer-Id" />
                                )}
                                <SingleSelect<EnumAccountGroup>
                                    {...binder.bindToAutoConfirmed('account_group')}
                                    items={minimalAccountGroupOptions}
                                    label={'Kontengruppe'}
                                    fullWidth
                                />
                                <SingleSelect<boolean>
                                    items={preferPaymentOptions}
                                    {...binder.bindToAutoConfirmed('prefer_direct_payment')}
                                    label={i18n('Zahlungsart')}
                                    fullWidth
                                />
                            </GridColumns>
                            <GridColumns spans={[6, 6]}>
                                <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                                    layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                                    {...binder.bindToLinkProperty(linkPaymentTermsRevenue.linkApi)}
                                    {...linkPaymentTermsRevenue.events}
                                    columns={paymentTermsColumns}
                                    label="Zahlungsbedingung Berechnung"
                                />
                                <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                                    layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                                    {...binder.bindToLinkProperty(linkPaymentTermsExpense.linkApi)}
                                    {...linkPaymentTermsExpense.events}
                                    columns={paymentTermsColumns}
                                    label="Zahlungsbedingung Vergütung"
                                />
                            </GridColumns>
                        </Grid>
                        {debtorInfo !== undefined && (
                            <Card
                                name="debitor"
                                collapsable={false}
                                key="debitor"
                                label="Debitorische Zahlungsinformationen"
                                badges={debitorBadges}
                                additionalHeaderAlignment={'left'}
                            >
                                <FrameEditPaymentInfo
                                    data={debtorInfo}
                                    meta={undefined}
                                    requestUpdateState={(u) => requestUpdatePaymentInfo(u, debtorInfo.id)}
                                    apiResourcePath={`${baseApiObjectPath}/payment_info`}
                                    stateQueueDepth={props.stateQueueDepth}
                                    refetchData={props.refetchData}
                                    canEdit={props.mode !== EditFrameMode.View}
                                />
                            </Card>
                        )}
                        {creditorInfo !== undefined && (
                            <Card
                                name="creditor"
                                collapsable={false}
                                label="Kreditorische Zahlungsinformationen"
                                key="creditor"
                                badges={creditorBadges}
                                additionalHeaderAlignment={'left'}
                            >
                                <FrameEditPaymentInfo
                                    data={creditorInfo}
                                    meta={undefined}
                                    apiResourcePath={`${baseApiObjectPath}/payment_info`}
                                    requestUpdateState={(u) => requestUpdatePaymentInfo(u, creditorInfo.id)}
                                    stateQueueDepth={props.stateQueueDepth}
                                    refetchData={props.refetchData}
                                    canEdit={props.mode !== EditFrameMode.View}
                                />
                            </Card>
                        )}
                        <Card
                            name={'invoice_print_option_settings'}
                            collapsable={false}
                            label={'Zusätzliche Belege beim Rechnungsdruck'}
                            key={'invoice_print_option_settings'}
                        >
                            <Grid columns={12}>
                                <GridColumns spans={[4, 4, 4]}>
                                    <Select<EnumPrintoutMergeStrategy>
                                        items={mergeStrategyOptions}
                                        {...binder.bindToAutoConfirmed('merge_strategy')}
                                        label="Modellierung der Anhänge"
                                    />
                                    <UnlabeledField>
                                        <Checkbox
                                            {...binder.bindToAutoConfirmed(
                                                'include_order_attachment_in_invoice_print_option',
                                            )}
                                            label={i18n('Lieferscheine/Handelspapiere')}
                                        />
                                    </UnlabeledField>
                                    <UnlabeledField>
                                        <Checkbox
                                            {...binder.bindToAutoConfirmed(
                                                'include_weighing_slip_attachment_in_invoice_print_option',
                                            )}
                                            label={i18n('Wiegescheine')}
                                        />
                                    </UnlabeledField>
                                </GridColumns>
                            </Grid>
                        </Card>
                    </RailSection>
                )}

                {(isCarrier || isDisposer || isProducer) && (
                    <RailSection
                        id="registration-numbers"
                        title="Registriernummern"
                        subtitle={registrationNumbersSummary(apiAddress)}
                    >
                        <Grid columns={9}>
                            <GridColumns spans={[3, 3, 3]}>
                                {isProducer && (
                                    <TextInput
                                        {...binder.bindToProp<string>('producer_number')}
                                        locked={!isProducer}
                                        label={i18n('Erzeugernummer')}
                                    />
                                )}
                                {isDisposer && (
                                    <TextInput
                                        {...binder.bindToProp<string>('disposer_number')}
                                        locked={!isDisposer}
                                        label={i18n('Entsorgernummer')}
                                    />
                                )}
                                {isCarrier && (
                                    <TextInput
                                        {...binder.bindToProp<string>('carrier_number')}
                                        locked={!isCarrier}
                                        label={i18n('Beförderernummer')}
                                    />
                                )}
                            </GridColumns>
                        </Grid>
                    </RailSection>
                )}

                <RailSection
                    id="remarks-section"
                    title="Bemerkung"
                    subtitle={remarksSummary(apiAddress)}
                    defaultOpened={!isNewAddress}
                >
                    <FlexRow maximize grow>
                        <TextInput
                            {...binder.bindToProp('remarks')}
                            inputSize={3}
                            label={''}
                            placeholder={'Interne Bemerkung zu dieser Adresse …'}
                        />
                    </FlexRow>
                </RailSection>

                <RailSection
                    id="links"
                    title="Adressverknüpfungen"
                    count={contactsCount + customersCount + (isCustomer ? locationsCount : 0)}
                    subtitle={linksSummary([
                        { label: i18n('Ansprechpartner'), count: contactsCount },
                        { label: i18n('Kunden'), count: customersCount },
                        ...(isCustomer ? [{ label: i18n('Leistungsorte'), count: locationsCount }] : []),
                    ])}
                    defaultOpened={!isNewAddress}
                >
                    <MultiPageView name="Related">{...linkLists}</MultiPageView>
                </RailSection>
            </RailLayout>
        </EditFrameShell>
    );
}
